import isNil from 'lodash/isNil';
import type {
	Status,
	Transition,
	WorkflowErrorsMap,
	Transitions,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import {
	errorSeverity,
	START_NODE_ID,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';

import { type Point, ValidationFlag } from './toPortInfo.tsx';
import type {
	ListEdgeDetails,
	ListEdgeType,
	ListNodeDetails,
	Positionable,
} from './ListEdgeDetails.tsx';
import type { Direction } from './directionKeys.tsx';

const orderStatuses = (statuses: Status[]): Status[] =>
	[...statuses].sort((a, b) => {
		if (a.layout === undefined || b.layout === undefined) return 0;
		if (a.layout.x !== b.layout.x) return a.layout.x - b.layout.x;
		return a.layout.y - b.layout.y;
	});

const getEdge = (
	transition: Transition,
	type: ListEdgeType,
	parentId: string | undefined,
	errors: WorkflowErrorsMap,
): ListEdgeDetails => {
	let validationFlag;

	const allErrors = (errors[transition.id] ?? []).concat(
		...transition.rules.filter((rule) => !isNil(errors[rule.id])).map((rule) => errors[rule.id]),
	);

	if (allErrors.length) {
		validationFlag = allErrors.some((error) => error.severity === errorSeverity.ERROR)
			? ValidationFlag.Error
			: ValidationFlag.Warn;
	}

	return {
		id: transition.id,
		transition,
		type,
		parentId,
		rulesCount: transition.rules.length,
		...(validationFlag ? { validationFlag } : {}),
		canDelete: type !== 'INITIAL',
	};
};

const getNestedEdges = (
	parentId: string,
	transitions: Transitions,
	errors: WorkflowErrorsMap,
): ListEdgeDetails[] => {
	const directedTransitions = transitions
		.filter(
			(transition) =>
				transition.toRef !== parentId &&
				transition.links &&
				transition.links.some((target) => target.fromRef === parentId),
		)
		.map((transition) =>
			getEdge(transition, transition.type === 'INITIAL' ? 'INITIAL' : 'DIRECTED', parentId, errors),
		);
	const loopedTransitions = transitions
		.filter(
			(transition) =>
				transition.toRef === parentId &&
				transition.links &&
				transition.links.some((target) => target.fromRef === parentId),
		)
		.map((transition) => getEdge(transition, 'LOOPED', parentId, errors));

	const globalTransitions = transitions
		.filter((transition) => !transition.links.length && transition.toRef === parentId)
		.map((transition) => getEdge(transition, 'GLOBAL', parentId, errors));

	return [...directedTransitions, ...loopedTransitions, ...globalTransitions];
};

const getGlobalLoops = (transitions: Transitions, errors: WorkflowErrorsMap): ListEdgeDetails[] =>
	transitions
		.filter((transition) => !transition.links.length && transition.toRef === undefined)
		.map((transition) => getEdge(transition, 'GLOBAL_LOOPED', undefined, errors));

export const getFlattenedStructure = (
	statuses: Status[],
	transitions: Transitions,
	startNodePosition: Point | undefined,
	errors: WorkflowErrorsMap = {},
	movingItem?: Positionable,
) => {
	const initialTransition = transitions.find((transition) => transition.type === 'INITIAL');
	const initialTransitionTarget = initialTransition?.toRef;

	const startItem: ListNodeDetails = {
		id: START_NODE_ID,
		isInitial: true,
		children: getNestedEdges(START_NODE_ID, transitions, errors),
		isMoving: movingItem?.id === START_NODE_ID,
		position: movingItem?.id === START_NODE_ID ? movingItem.position : startNodePosition,
		canDelete: false,
	};

	const statusItems: ListNodeDetails[] = orderStatuses(statuses).map((status) => {
		const statusErrors = errors?.[status.id] || [];
		const isMoving = movingItem?.id === status.id;

		const canDelete = status.id !== initialTransitionTarget;

		return {
			id: status.id,
			status,
			children: getNestedEdges(status.id, transitions, errors),
			...(statusErrors.length ? { validationFlag: ValidationFlag.Error } : {}),
			isMoving,
			position: isMoving ? movingItem.position : status.layout,
			canDelete,
		};
	});

	const globalLoops = getGlobalLoops(transitions, errors);

	return { nodes: [startItem, ...statusItems], globalLoops };
};

export const getMovedPoint = (point: Point, direction: Direction, distance: number) => {
	const updated = { ...point };

	if (direction === 'up') {
		updated.y -= distance;
	}
	if (direction === 'down') {
		updated.y += distance;
	}
	if (direction === 'left') {
		updated.x -= distance;
	}
	if (direction === 'right') {
		updated.x += distance;
	}

	return updated;
};
